import type { FormEvent, KeyboardEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  BadgeCheck,
  CreditCard,
  Crown,
  ExternalLink,
  LayoutDashboard,
  LogOut,
  Mail,
  Menu,
  PackageOpen,
  ReceiptText,
  RefreshCw,
  Settings,
  ShieldCheck,
  UserRound,
  X,
  Gift,
} from "lucide-react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Seo } from "../components/Seo";
import { SubscriptionCard } from "../components/SubscriptionCard";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import {
  AndroidIcon,
  IosIcon,
  MacosIcon,
  WindowsIcon,
} from "../components/icons/FeatureIcons";
import {
  ApiError,
  userApi,
  type AccountOrder,
  type AccountOverview,
  type AccountSubscription,
  type PublicPlan,
  type PublicRelease,
  type PaymentConfiguration,
} from "../lib/api";
import { useI18n } from "../i18n";
import { InvitationPanel } from '../components/InvitationPanel';
import { captureInvite, parseInviteCode, readInvite, saveInvite } from '../lib/invitations';
import { accountProfilePolicy } from '../lib/account-profile';
import { ProfileHelp } from '../components/ProfileHelp';
import { CheckoutDialog, type CheckoutTarget } from '../components/CheckoutDialog';
import { MembershipShop } from '../components/MembershipShop';

const bytesToGb = (bytes: number) => `${(bytes / 1024 ** 3).toFixed(2)} GB`;
const date = (value: string | null, locale: string) => value
  ? new Intl.DateTimeFormat(locale, { year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(value))
  : "—";
const dateTime = (value: string, locale: string) => new Intl.DateTimeFormat(locale, {
  year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false,
}).format(new Date(value));
const errorMessage = (caught: unknown, fallback: string) => caught instanceof TypeError ? fallback : caught instanceof Error ? caught.message : fallback;
const statusText: Record<string, string> = { pending: "待支付", paid: "已支付", cancelled: "已取消", refunded: "已退款" };

type DashboardSection = "overview" | "subscription" | "plans" | "orders" | "invitations";

const dashboardNavigation: Array<{ id: DashboardSection; path: string; label: string; icon: typeof LayoutDashboard }> = [
  { id: "overview", path: "/account", label: "账户总览", icon: LayoutDashboard },
  { id: "subscription", path: "/account/subscription", label: "订阅管理", icon: ShieldCheck },
  { id: "plans", path: "/account/plans", label: "会员", icon: Crown },
  { id: "orders", path: "/account/orders", label: "订单记录", icon: ReceiptText },
  { id: "invitations", path: "/account/invitations", label: "邀请免费加速", icon: Gift },
];

const pageDescriptions: Record<DashboardSection, string> = {
  overview: "账户权益、个人信息与常用入口。",
  subscription: "查看连接状态、流量信息，并安全管理你的订阅配置。",
  plans: "选择会员或流量包，让加速继续。",
  orders: "查看套餐订单的金额、状态与创建时间。",
  invitations: "邀请好友注册，领取会员时长与高速流量。",
};

const clientChannels = [
  {
    platform: "windows",
    name: "Windows",
    icon: WindowsIcon,
    requirement: "适用于 Windows 10 / 11",
    fallbackUrl: "https://github.com/clash-verge-rev/clash-verge-rev/releases/latest",
    btnLabel: "下载客户端",
  },
  {
    platform: "macos",
    name: "macOS",
    icon: MacosIcon,
    requirement: "适用于 macOS 12+",
    fallbackUrl: "https://github.com/clash-verge-rev/clash-verge-rev/releases/latest",
    btnLabel: "下载客户端",
  },
  {
    platform: "ios",
    name: "iOS",
    icon: IosIcon,
    requirement: "适用于 iPhone / iPad",
    fallbackUrl: "https://apps.apple.com/app/stash/id1596063349",
    btnLabel: "App Store 下载",
  },
  {
    platform: "android",
    name: "Android",
    icon: AndroidIcon,
    requirement: "适用于 Android 10+",
    fallbackUrl: "https://github.com/MetaCubeX/ClashMetaForAndroid/releases",
    btnLabel: "下载客户端",
  },
] as const;

type SettingsTab = "profile" | "plan";

export function AccountPage() {
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { locale, t } = useI18n();
  const [mode, setMode] = useState<"login" | "register">("login");
  const [inviteCode, setInviteCode] = useState('');
  const [inviteNotice, setInviteNotice] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [overview, setOverview] = useState<AccountOverview | null>(null);
  const [subscription, setSubscription] = useState<AccountSubscription | null>(null);
  const [orders, setOrders] = useState<AccountOrder[]>([]);
  const [plans, setPlans] = useState<PublicPlan[]>([]);
  const [payment, setPayment] = useState<PaymentConfiguration | null>(null);
  const [releases, setReleases] = useState<PublicRelease[]>([]);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState<SettingsTab>("profile");
  const [displayName, setDisplayName] = useState("");
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState("");
  const [profilePolicyStatus, setProfilePolicyStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [profileReload, setProfileReload] = useState(0);
  const [logoutBusy, setLogoutBusy] = useState(false);
  const [checkoutTarget,setCheckoutTarget]=useState<CheckoutTarget|null>(null);
  const returnedOrder=useRef<string|null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const activeNavigation = dashboardNavigation.find(({ path }) => path === location.pathname);
  const activeSection = activeNavigation?.id ?? "overview";

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    setError(null);
    try {
      const [account, orderList, planList, sub, relList, payConfig] = await Promise.all([
        userApi.overview(),
        userApi.orders(),
        userApi.plans(),
        userApi.subscription().catch(() => null),
        userApi.releases().catch(() => []),
        userApi.paymentConfiguration().catch(() => null),
      ]);
      setOverview(account);
      setDisplayName(account.user.displayName ?? "");
      setOrders(orderList);
      setPlans(planList.filter(plan => plan.productKind === 'membership' || plan.productKind === 'topup'));
      setSubscription(sub);
      setReleases(relList);
      setPayment(payConfig);
      setProfilePolicyStatus('ready');
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) {
        setOverview(null);
        setSubscription(null);
        setOrders([]);
        setSettingsOpen(false);
      } else {
        setError(errorMessage(caught, "无法连接账户服务，请检查网络后重试。"));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  useEffect(()=>{
    const id=params.get('payment_order');
    if(overview && id && /^[a-f0-9-]{36}$/.test(id) && returnedOrder.current!==id){returnedOrder.current=id;setCheckoutTarget({orderId:id});}
  },[overview,params]);
  useEffect(() => {
    if (!settingsOpen) return;
    let current = true;
    let inFlight = false;
    setProfilePolicyStatus('loading');
    const refresh = async () => {
      if (inFlight || document.visibilityState === 'hidden') return;
      inFlight = true;
      try {
        const account = await userApi.overview();
        if (current) {
          // Do not overwrite the nickname being edited while refreshing rights.
          setOverview(previous => previous ? { ...account, user: { ...account.user, displayName: previous.user.displayName } } : previous);
          setProfilePolicyStatus('ready');
        }
      } catch (caught) {
        if (current && caught instanceof ApiError && caught.status === 401) {
          setOverview(null); setSubscription(null); setSettingsOpen(false);
        } else if (current) setProfilePolicyStatus('error');
      } finally {
        inFlight = false;
      }
    };
    void refresh();
    const timer = window.setInterval(() => void refresh(), 30_000);
    const onVisible = () => { if (document.visibilityState === 'visible') void refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      current = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [settingsOpen, profileReload]);
  useEffect(() => {
    captureInvite(location.search);
    const cached = readInvite();
    setInviteCode(cached);
    if (cached) setMode('register');
  }, [location.search]);

  const editInvite = (value: string) => {
    const code = parseInviteCode(value) ?? value.trim().toUpperCase();
    setInviteCode(code);
    saveInvite(parseInviteCode(code) ?? '');
    setInviteNotice('');
  };
  const pasteInvite = async () => {
    try {
      const code = parseInviteCode(await navigator.clipboard.readText());
      if (!code) { setInviteNotice('剪贴板中没有有效的邀请码或邀请链接。'); return; }
      editInvite(code); setInviteNotice('邀请码已保存，注册时会自动使用。');
    } catch { setInviteNotice('请手动粘贴邀请码，浏览器未允许读取剪贴板。'); }
  };

  useEffect(() => {
    if (!settingsOpen) return;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") setSettingsOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("keydown", closeOnEscape);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, [settingsOpen]);

  useEffect(() => {
    if (!activeNavigation) return;
    setMobileNavOpen(false);
    window.scrollTo({ top: 0, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  }, [activeNavigation, location.pathname]);

  const auth = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setSubmitting(true);
    setError(null);
    try {
      const email = String(form.get("email"));
      const password = String(form.get("password"));
      if (mode === "register") {
        if (form.get("legalConsent") !== "on") throw new Error("请先阅读并同意用户协议和隐私政策。");
        if (inviteCode && !parseInviteCode(inviteCode)) throw new Error('邀请码无效，请检查后重试。');
        await userApi.register(email, password, String(form.get("displayName")), true, inviteCode);
        saveInvite(''); setInviteCode('');
      } else {
        await userApi.login(email, password);
      }
      await load();
    } catch (caught) {
      setError(errorMessage(caught, "无法连接账户服务，请稍后重试。"));
    } finally {
      setSubmitting(false);
    }
  };

  const createOrder = async (planId: string, quantity = 1) => {
    const plan=plans.find(p=>p.id===planId);
    if(plan)setCheckoutTarget({plan,quantity});
  };

  const logout = async () => {
    setLogoutBusy(true);
    setError(null);
    try {
      await userApi.logout();
      setOverview(null);
      setSubscription(null);
      setOrders([]);
      setPlans([]);
      setMode("login");
    } catch (caught) {
      setError(errorMessage(caught, "退出登录失败，请稍后重试。"));
    } finally {
      setLogoutBusy(false);
    }
  };

  const navigateTo = (id: DashboardSection) => {
    setMobileNavOpen(false);
    const destination = dashboardNavigation.find((item) => item.id === id);
    if (destination && destination.path !== location.pathname) navigate(destination.path);
  };

  const openSettings = (tab: SettingsTab = "profile") => {
    setSettingsTab(tab);
    setProfileMessage("");
    setProfilePolicyStatus('loading');
    setSettingsOpen(true);
    setMobileNavOpen(false);
  };

  const saveProfile = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setProfileSaving(true);
    setProfileMessage("");
    setError(null);
    try {
      const user = await userApi.updateProfile(displayName.trim());
      setOverview((current) => current ? { ...current, user } : current);
      setDisplayName(user.displayName ?? "");
      setProfileMessage("个人资料已保存");
    } catch (caught) {
      setError(errorMessage(caught, "保存失败，请检查网络后重试。"));
    } finally {
      setProfileSaving(false);
    }
  };

  const handleDialogKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Tab") return;
    const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
    );
    if (!focusable?.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const preferredPlan = params.get("plan");
  const displayInitial = (overview?.user.displayName || overview?.user.email || "云").slice(0, 1).toUpperCase();

  const subUsed = (subscription?.uploadBytes ?? 0) + (subscription?.downloadBytes ?? 0);
  const subRemaining = subscription?.totalBytes ? Math.max(0, subscription.totalBytes - subUsed) : 0;
  const activePlan = overview?.activePlan || (subscription?.totalBytes ? {
    name: subscription.nodeName ? `${subscription.nodeName}套餐` : t("节点订阅套餐"),
    totalBytes: subscription.totalBytes,
    remainingBytes: subRemaining,
    validUntil: subscription.expiresAt ?? "",
  } : null);

  const remainingBytes = (overview?.remainingBytes && overview.remainingBytes > 0)
    ? overview.remainingBytes
    : (activePlan ? activePlan.remainingBytes : 0);

  const activeUntil = overview?.activeUntil || activePlan?.validUntil || null;
  const profilePolicy = overview ? accountProfilePolicy(overview) : null;
  // Request state controls the sync indicator, not already fetched values.
  const profilePolicyReady = profilePolicy != null;

  if (!activeNavigation) return <Navigate replace to="/account" />;

  return (
    <div className="account-workspace account-app-style">
      <Seo />
      {mobileNavOpen && <button type="button" className="account-drawer-scrim" aria-label={t("关闭账户菜单")} onClick={() => setMobileNavOpen(false)} />}
      <aside className={`account-sidebar${mobileNavOpen ? " is-open" : ""}`} aria-label={t("账户导航")}>
        <div className="account-sidebar-brand-row">
          <Link className="account-brand" to="/" aria-label="Flux cloud">
            <img src="/logo.png" alt="Flux cloud" className="account-brand-icon" />
            <span>Flux cloud</span>
            <small>{t("账户中心")}</small>
          </Link>
          <button className="account-sidebar-close" aria-label={t("关闭菜单")} onClick={() => setMobileNavOpen(false)}><X /></button>
        </div>

        {overview ? (
          <>
            <nav className="account-nav" aria-label={t("账户功能")}>
              {dashboardNavigation.map(({ id, path, label, icon: Icon }) => (
                <Link
                  key={id}
                  to={path}
                  className={activeSection === id ? "is-active" : ""}
                  aria-current={activeSection === id ? "page" : undefined}
                  onClick={() => setMobileNavOpen(false)}
                >
                  <Icon aria-hidden="true" />
                  <span>{t(label)}</span>
                </Link>
              ))}
            </nav>
            <div className="account-sidebar-bottom">
              <div className="account-sidebar-user-row">
                <button
                  type="button"
                  className="account-sidebar-user"
                  onClick={() => openSettings()}
                  aria-label={t("账户设置")}
                  title={t("账户设置")}
                >
                  <UserRound aria-hidden="true" />
                  <span className="account-sidebar-user-copy">
                    <strong>{t('我的')}</strong>
                  </span>
                </button>
                <button
                  type="button"
                  className="account-sidebar-logout-icon"
                  disabled={logoutBusy}
                  onClick={() => void logout()}
                  title={t("退出登录")}
                  aria-label={t("退出登录")}
                >
                  <LogOut aria-hidden="true" />
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <nav className="account-nav account-nav-public" aria-label={t("云流导航")}>
              <span className="account-nav-heading">{t("云流账户")}</span>
              <a className="is-active" href="#account-auth"><UserRound aria-hidden="true" /><span>{t("登录与注册")}</span></a>
              <Link to="/pricing"><CreditCard aria-hidden="true" /><span>{t("会员")}</span></Link>
              <Link to="/download"><PackageOpen aria-hidden="true" /><span>{t("客户端下载")}</span></Link>
            </nav>
          </>
        )}
      </aside>

      <div className="account-main-column">
        <header className="account-mobile-bar">
          <Link className="account-brand" to="/" aria-label="Flux cloud">
            <img src="/logo.png" alt="Flux cloud" className="account-brand-icon" />
            <span>Flux cloud</span>
            <small>{t("账户中心")}</small>
          </Link>
          <div className="account-mobile-actions"><LanguageSwitcher className="account-language-switch" /><button type="button" className="account-menu-toggle" aria-label={t("打开账户菜单")} aria-expanded={mobileNavOpen} onClick={() => setMobileNavOpen(true)}><Menu /></button></div>
        </header>

        <main className="account-main" id="account-main">
          {loading ? (
            <div className="account-loading" role="status"><RefreshCw className="spin" /><span>{t("正在加载账户信息")}</span></div>
          ) : !overview ? (
            <section className="account-auth-layout" id="account-auth">
              <div className="account-auth-top-actions">
                <LanguageSwitcher className="account-language-switch" />
              </div>
              <div className="account-auth-intro">
                <p className="account-auth-kicker"><ShieldCheck aria-hidden="true" />{t("云流账户")}</p>
                <h1>{t("让熟悉的内容，")}<br />{t("随时与你相连。")}</h1>
                <p>{t("登录后管理订阅、流量权益和订单，也可以在这里完成客户端配置。")}</p>
                <ul>
                  <li><BadgeCheck aria-hidden="true" />{t("订阅与套餐集中管理")}</li>
                  <li><BadgeCheck aria-hidden="true" />{t("订单状态随时查看")}</li>
                  <li><BadgeCheck aria-hidden="true" />{t("账户信息由你掌控")}</li>
                </ul>
              </div>
              <div className="account-auth-card">
                <div className="account-auth-tabs" role="tablist" aria-label={t("登录或注册")}>
                  <button type="button" role="tab" aria-selected={mode === "login"} className={mode === "login" ? "is-active" : ""} onClick={() => { setMode("login"); setError(null); }}>{t("登录")}</button>
                  <button type="button" role="tab" aria-selected={mode === "register"} className={mode === "register" ? "is-active" : ""} onClick={() => { setMode("register"); setError(null); }}>{t("创建账户")}</button>
                </div>
                <h2>{t(mode === "login" ? "欢迎回来" : "创建云流账户")}</h2>
                <p>{t(mode === "login" ? "使用你的邮箱和密码继续。" : "注册后即可管理个人订阅。")}</p>
                <form onSubmit={(event) => void auth(event)}>
                  {mode === "register" && <label>{t("昵称")}<input name="displayName" placeholder={t("如何称呼你")} maxLength={80} required /></label>}
                  <label>{t("邮箱")}<input name="email" type="email" autoComplete="email" placeholder="name@example.com" required /></label>
                  <label>{t("密码")}<input name="password" type="password" autoComplete={mode === "login" ? "current-password" : "new-password"} placeholder={t("至少 8 位")} minLength={8} required /></label>
                  {mode === 'register' && <div className="invitation-register-field"><label>{t('好友邀请码（选填）')}<input value={inviteCode} onChange={e => editInvite(e.target.value)} onPaste={e => { const code = parseInviteCode(e.clipboardData.getData('text')); if (code) { e.preventDefault(); editInvite(code); } }} autoComplete="off" placeholder={t('输入邀请码或粘贴邀请链接')} maxLength={10} /></label><button type="button" onClick={() => void pasteInvite()} disabled={submitting}>{t('粘贴并保存邀请码')}</button>{inviteCode && <small>{t('已记住邀请码，注册成功后自动清除。')}</small>}{inviteNotice && <small role="status">{t(inviteNotice)}</small>}</div>}
                  {mode === "register" && <label className="account-legal-consent"><input name="legalConsent" type="checkbox" required /><span>{t("我已阅读并同意")} <Link target="_blank" rel="noopener noreferrer" to={`/terms?lang=${locale === "en-US" ? "en" : "zh"}`}>{t("用户协议")}</Link> {t("和")} <Link target="_blank" rel="noopener noreferrer" to={`/privacy?lang=${locale === "en-US" ? "en" : "zh"}`}>{t("隐私政策")}</Link></span></label>}
                  {error && <div className="account-error" role="alert">{t(error)}</div>}
                  <button className="account-primary-button" type="submit" disabled={submitting}>{t(submitting ? "处理中…" : mode === "login" ? "登录账户" : "创建账户")}<ArrowRight aria-hidden="true" /></button>
                </form>
                <div className="account-auth-foot"><ShieldCheck aria-hidden="true" /><span>{t("账户连接使用加密会话保护")}</span></div>
              </div>
            </section>
          ) : (
            <>
              <div className="account-content-width">
                {error && <div className="account-error account-error-banner" role="alert">{error}</div>}
                <section className="account-page-heading">
                  <div className="account-heading-person">
                    <div>
                      <h1>{activeSection === "overview" ? t("你好，{name}", { name: overview.user.displayName || t("云流用户") }) : t(activeNavigation.label)}</h1>
                      <p>{activeSection === "overview" ? overview.user.email : t(pageDescriptions[activeSection])}</p>
                    </div>
                  </div>
                  <div className="account-heading-actions">
                    <LanguageSwitcher className="account-language-switch" />
                  </div>
                </section>

                {activeSection === "overview" && (
                  <>
                    <section className="account-overview-grid" aria-label={t("账户摘要")}>
                      <article className="account-entitlement-panel">
                        <div className="account-panel-title">
                          <div className="account-panel-symbol"><ShieldCheck aria-hidden="true" /></div>
                          <div>
                            <h2>{activePlan ? t(activePlan.name) : plans.length > 0 ? t("可选套餐") : t("云流账户权益")}</h2>
                            <p>{activePlan ? t("当前生效的流量权益") : plans.length > 0 ? t("当前暂无生效套餐，{count} 款套餐可选", { count: plans.length }) : t("暂无生效套餐")}</p>
                          </div>
                          <span className={`account-plan-state${activePlan ? " is-ready" : ""}`}><i />{t(activePlan ? "生效中" : plans.length > 0 ? "可选择" : "未开通")}</span>
                        </div>
                        <div className="account-entitlement-metrics">
                          <div><span>{t("账户剩余流量")}</span><strong>{bytesToGb(remainingBytes)}</strong></div>
                          <div><span>{t("本套餐额度")}</span><strong>{activePlan ? bytesToGb(activePlan.totalBytes) : "—"}</strong></div>
                          <div><span>{t("有效期至")}</span><strong>{date(activeUntil, locale)}</strong></div>
                        </div>
                        {activePlan && <progress value={Math.max(0, activePlan.totalBytes - activePlan.remainingBytes)} max={activePlan.totalBytes} aria-label={t("套餐已用流量比例")} />}
                        <button className="account-panel-link" type="button" onClick={() => navigateTo("plans")}>{t("查看套餐与续费")}<ArrowRight aria-hidden="true" /></button>
                      </article>

                      <article className="account-profile-panel">
                        <div className="account-profile-panel-head"><span><UserRound aria-hidden="true" /></span><button type="button" onClick={() => openSettings("profile")}>{t("编辑资料")}<ArrowRight aria-hidden="true" /></button></div>
                        <h2>{t("个人信息")}</h2>
                        <dl>
                          <div><dt>{t("登录邮箱")}</dt><dd><Mail aria-hidden="true" />{overview.user.email}</dd></div>
                        </dl>
                      </article>
                    </section>
                    <section className="account-quick-links" aria-label={t("常用功能")}>
                      <Link to="/account/invitations"><span><Gift aria-hidden="true" /></span><div><strong>{t('邀请免费加速')}</strong><small>{t('每位好友 · 24 小时 + 3GB')}</small></div><ArrowRight aria-hidden="true" /></Link>
                      <Link to="/account/subscription" onClick={() => setMobileNavOpen(false)}><span><ShieldCheck aria-hidden="true" /></span><div><strong>{t("订阅管理")}</strong><small>{t("管理连接配置与订阅链接")}</small></div><ArrowRight aria-hidden="true" /></Link>
                      <Link to="/account/plans" onClick={() => setMobileNavOpen(false)}><span><Crown aria-hidden="true" /></span><div><strong>{t("会员")}</strong><small>{t("选择会员或流量包，让加速继续。")}</small></div><ArrowRight aria-hidden="true" /></Link>
                      <Link to="/account/orders" onClick={() => setMobileNavOpen(false)}><span><ReceiptText aria-hidden="true" /></span><div><strong>{t("订单记录")}</strong><small>{t("查看支付与订单状态")}</small></div><ArrowRight aria-hidden="true" /></Link>
                    </section>
                    {plans.length > 0 && (
                      <section className="account-home-plans" aria-labelledby="account-home-plans-title">
                        <header className="account-home-plans-heading">
                          <div><h2 id="account-home-plans-title">{t("可选套餐")}</h2><p>{t("当前有 {count} 款套餐可选，创建订单后可在订单记录中查看。", { count: plans.length })}</p></div>
                          <Link to="/account/plans" onClick={() => setMobileNavOpen(false)}>{t("查看全部")}<ArrowRight aria-hidden="true" /></Link>
                        </header>
                        <div className="account-plan-grid">
                          {plans.slice(0, 3).map((plan) => (
                            <article className={plan.id === preferredPlan || plan.recommended ? "is-highlighted" : ""} key={plan.id}>
                              <div className="account-plan-card-head"><h3>{t(plan.name)}</h3></div>
                              <p>{t(plan.description)}</p>
                              <strong className="account-plan-traffic">{plan.trafficGb}<small> GB</small></strong>
                              <div className="account-plan-card-bottom"><span><b>¥{plan.priceCny}</b>{plan.productKind === "topup" ? t(" / 份，随会员周期到期") : t(" / {days}天", { days: plan.validityDays })}</span><button type="button" disabled={submitting || (plan.requiresMembership && !overview.membership?.canTopup)} onClick={() => void createOrder(plan.id)}>{t(plan.requiresMembership && !overview.membership?.canTopup ? "请先开通会员" : submitting ? "处理中…" : "创建订单")}<ArrowRight aria-hidden="true" /></button></div>
                            </article>
                          ))}
                        </div>
                      </section>
                    )}
                  </>
                )}

                {activeSection === 'invitations' && <InvitationPanel key={overview.user.id} onClaim={() => load(true)} />}
                {activeSection === "subscription" && (
                  <section className="account-page-section">
                    <SubscriptionCard subscription={subscription} onRefresh={() => void load(true)} />
                    <section className="account-download-section" aria-labelledby="download-channels-title">
                      <div className="account-download-heading">
                        <h3 id="download-channels-title">{t("多平台客户端下载")}</h3>
                        <p>{t("选择与你的设备匹配的客户端，支持主流桌面端与移动端。")}</p>
                      </div>
                      <div className="account-download-grid">
                        {clientChannels.map(({ platform, name, icon: Icon, requirement, fallbackUrl, btnLabel }) => {
                          const release = releases.find((r) => r.platform === platform);
                          const downloadUrl = release?.downloadUrl || fallbackUrl;
                          const buttonText = release ? t("下载 v{version}", { version: release.version }) : t(btnLabel);
                          return (
                            <div className="account-download-card" key={platform}>
                              <div className="account-download-info">
                                <span className="account-download-icon"><Icon /></span>
                                <div className="account-download-text">
                                  <h4>{name}</h4>
                                  <p>{t(requirement)}</p>
                                </div>
                              </div>
                              <a
                                className="account-download-btn"
                                href={downloadUrl}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {buttonText}
                                <ExternalLink size={13} aria-hidden="true" />
                              </a>
                            </div>
                          );
                        })}
                      </div>
                    </section>
                  </section>
                )}

                {activeSection === "plans" && (
                  <MembershipShop plans={plans} membership={overview.membership} payment={payment} preferredPlan={preferredPlan} onCheckout={setCheckoutTarget} />
                )}

                {activeSection === "orders" && (
                  <section className="account-page-section account-orders-section">
                    <header className="account-section-heading">
                      <div><h2>{t("订单记录")}</h2><p>{t("共 {count} 笔订单，最近 100 笔会显示在这里。", { count: overview.orderCount })}</p></div>
                      <button type="button" className="account-subtle-button" onClick={() => void load(true)}><RefreshCw aria-hidden="true" />{t("刷新记录")}</button>
                    </header>
                    <div className="account-orders-table">
                      <div className="account-order-head"><span>{t("订单编号")}</span><span>{t("套餐")}</span><span>{t("金额")}</span><span>{t("状态")}</span><span>{t("创建时间")}</span></div>
                      {orders.map((order) => <div className="account-order-row" key={order.id}>
                        <span className="account-order-number">{order.orderNo}</span>
                        <span className="account-order-plan">{t(order.planName)}<small>{bytesToGb(order.trafficBytes)}</small></span>
                        <span>¥{(order.amountMinor / 100).toFixed(2)}</span>
                        <span><span className={`account-order-status ${order.status}`}>{t(order.receiptState==='review'?'付款待核对':statusText[order.status] ?? order.status)}</span>
                          {order.paymentMethod && order.paymentMethod!=='manual' && <button type="button" className="account-order-payment-button" onClick={()=>setCheckoutTarget({orderId:order.id})}>{t(order.status==='pending'?'查看支付':'支付详情')}</button>}</span>
                        <span>{dateTime(order.createdAt, locale)}</span>
                      </div>)}
                      {orders.length === 0 && <div className="account-empty-orders"><ReceiptText aria-hidden="true" /><strong>{t("还没有订单")}</strong><span>{t("选择套餐后，订单会显示在这里。")}</span><button type="button" onClick={() => navigateTo("plans")}>{t("查看可选套餐")}<ArrowRight aria-hidden="true" /></button></div>}
                    </div>
                  </section>
                )}
                <footer className="account-content-footer"><span>© {new Date().getFullYear()} 云流加速器</span></footer>
              </div>
            </>
          )}
        </main>
      </div>

      {checkoutTarget && overview && <CheckoutDialog target={checkoutTarget} onClose={()=>{setCheckoutTarget(null);void load(true);}} onPaid={()=>{void load(true);}}/>}
      {settingsOpen && overview && (
        <div className="account-dialog-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) setSettingsOpen(false); }}>
          <div
            className="account-settings-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="account-settings-title"
            tabIndex={-1}
            ref={dialogRef}
            onKeyDown={handleDialogKeyDown}
          >
            <header className="account-dialog-heading">
              <div><span className="account-dialog-icon"><Settings aria-hidden="true" /></span><div><h2 id="account-settings-title">{t("账户设置")}</h2><p>{t("管理个人资料与当前套餐信息。")}</p></div></div>
              <button type="button" className="account-dialog-close" aria-label={t("关闭账户设置")} onClick={() => setSettingsOpen(false)}><X aria-hidden="true" /></button>
            </header>
            <div className="account-settings-tabs" role="tablist" aria-label={t("账户设置分类")}>
              <button type="button" role="tab" id="settings-profile-tab" aria-controls="settings-profile-panel" aria-selected={settingsTab === "profile"} className={settingsTab === "profile" ? "is-active" : ""} onClick={() => setSettingsTab("profile")}><UserRound aria-hidden="true" />{t("个人资料")}</button>
              <button type="button" role="tab" id="settings-plan-tab" aria-controls="settings-plan-panel" aria-selected={settingsTab === "plan"} className={settingsTab === "plan" ? "is-active" : ""} onClick={() => setSettingsTab("plan")}><CreditCard aria-hidden="true" />{t("套餐与流量")}</button>
            </div>
            {settingsTab === "profile" ? (
              <form className="account-settings-panel" id="settings-profile-panel" role="tabpanel" aria-labelledby="settings-profile-tab" onSubmit={(event) => void saveProfile(event)}>
                <div className="account-settings-avatar"><span className="account-avatar">{displayInitial}</span><div><strong>{overview.user.displayName || t("云流用户")}</strong><small>{t("账户资料")}</small></div></div>
                <label>{t("昵称")}<input value={displayName} onChange={(event) => setDisplayName(event.target.value)} maxLength={80} placeholder={t("填写你希望使用的称呼")} required /></label>
                <label>{t("登录邮箱")}<div className="account-readonly-field"><Mail aria-hidden="true" /><input value={overview.user.email} readOnly aria-readonly="true" /><span>{t("不可修改")}</span></div></label>
                <dl className="account-profile-policy" aria-busy={profilePolicyStatus === 'loading'}>
                  <div><dt>{t('会员等级')}</dt><dd><span className={`account-profile-level${profilePolicyReady && profilePolicy?.level === '高级会员' ? ' is-premium' : ''}`}>{profilePolicy?.level ? t(profilePolicy.level) : '–'}</span></dd></div>
                  <div><dt>{t('当前网络带宽')}</dt><dd><strong>{profilePolicy?.peakMbps != null ? t('上下行各 {speed} Mbps', { speed: profilePolicy.peakMbps }) : '–'}</strong><ProfileHelp label={t('带宽说明')} message={t(overview.membership && !overview.membership.speedEnforced ? '账号共享上限，限速待节点生效。' : '账号共享上限，非实时测速。')} /></dd></div>
                  <div><dt>{t('同时在线设备')}</dt><dd><strong>{profilePolicy?.onlineDevices != null && profilePolicy.maxDevices != null ? t('{online} / {limit} 台', { online: profilePolicy.onlineDevices, limit: profilePolicy.maxDevices }) : '–'}</strong><ProfileHelp label={t('在线设备说明')} message={t('App 加速时自动占用名额，断开后释放；连接中会预留名额，异常退出最多90秒回收。第三方客户端尚未计入。')} /></dd></div>
                </dl>
                {profilePolicyStatus === 'error' && <div className="account-profile-sync-error" role="alert"><span>{t('权益同步失败，请重试。')}</span><button type="button" onClick={() => setProfileReload(value => value + 1)}>{t('重新同步')}</button></div>}
                {profileMessage && <p className="account-settings-success" role="status">{t(profileMessage)}</p>}
                {error && <p className="account-settings-error" role="alert">{t(error)}</p>}
                <footer className="account-dialog-actions"><button type="button" className="account-secondary-button" onClick={() => setSettingsOpen(false)}>{t("关闭")}</button><button type="submit" className="account-primary-button" disabled={profileSaving}>{t(profileSaving ? "保存中…" : "保存资料")}</button></footer>
              </form>
            ) : (
              <div className="account-settings-panel" id="settings-plan-panel" role="tabpanel" aria-labelledby="settings-plan-tab">
                <div className="account-settings-plan-name"><span className="account-plan-symbol"><ShieldCheck aria-hidden="true" /></span><div><small>{t("当前套餐")}</small><strong>{activePlan ? t(activePlan.name) : t("暂无有效套餐")}</strong></div><span className={`account-plan-state${activePlan ? " is-ready" : ""}`}><i />{t(activePlan ? "生效中" : "未开通")}</span></div>
                <div className="account-settings-plan-metrics">
                  <div><span>{t("账户剩余流量")}</span><strong>{bytesToGb(remainingBytes)}</strong></div>
                  <div><span>{t("套餐额度")}</span><strong>{activePlan ? bytesToGb(activePlan.totalBytes) : "—"}</strong></div>
                  <div><span>{t("有效期至")}</span><strong>{date(activeUntil, locale)}</strong></div>
                </div>
                <p className="account-settings-plan-note">{t("节点订阅的实时用量以“订阅管理”中的同步信息为准。")}</p>
                {overview.membership && <div className="account-settings-membership-detail">
                  <dl>
                    {overview.membership.active && <><div><dt>{t('高速剩余流量')}</dt><dd>{bytesToGb(overview.membership.highSpeedRemainingBytes)}</dd></div><div><dt>{t('会员有效期至')}</dt><dd>{date(overview.membership.expiresAt, locale)}</dd></div></>}
                     {overview.membership.highSpeedResetsAt && <div><dt>{t('下次高速流量发放')}</dt><dd>{dateTime(overview.membership.highSpeedResetsAt, locale)}</dd></div>}
                    <div><dt>{t('本月免费体验剩余')}</dt><dd>{bytesToGb(overview.membership.freeRemainingBytes)}</dd></div>
                    <div><dt>{t('免费额度重置时间')}</dt><dd>{dateTime(overview.membership.freeResetsAt, locale)}</dd></div>
                    {overview.membership.nextCycleStartsAt && <div><dt>{t('下个会员周期开始时间')}</dt><dd>{dateTime(overview.membership.nextCycleStartsAt, locale)}</dd></div>}
                    {overview.membership.scheduledUntil && overview.membership.scheduledUntil !== overview.membership.expiresAt && <div><dt>{t('会员权益累计至')}</dt><dd>{dateTime(overview.membership.scheduledUntil, locale)}</dd></div>}
                  </dl>
                  {overview.membership.accessReason === 'premium_exhausted' && <p className="account-settings-quota-warning" role="status">{t('高速额度已用完，当前会员周期停止加速，不使用免费额度。')}</p>}
                  {overview.membership.accessReason === 'free_exhausted' && <p className="account-settings-quota-warning" role="status">{t('免费额度已用完，次月北京时间零点恢复。')}</p>}
                   <p>{overview.membership.source === 'invitation' ? t('邀请会员每份24小时与3GB，按奖励周期生效。') : overview.membership.active ? t('付费会员每30天发放100GB，未用完不结转；有效期以所购套餐为准，续费排队生效。') : t('免费额度5GB，按北京自然月重置。')}</p>
                  {subscription?.billingMode === 'baseline' && <p>{t('当前连接仍使用原测试基线额度；本月免费钱包尚未接入该节点，实际可用流量以订阅管理为准。')}</p>}
                </div>}
                <footer className="account-dialog-actions"><button type="button" className="account-secondary-button" onClick={() => setSettingsOpen(false)}>{t("关闭")}</button><button type="button" className="account-primary-button" onClick={() => { setSettingsOpen(false); navigateTo("plans"); }}>{t("选择套餐")}</button></footer>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
